/* import React, { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Cloud, Server, Video, LayoutGrid } from "lucide-react";

const TimelineItem = ({ year, title, items, index, isLeft }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <div
      ref={ref}
      className={`flex w-full mb-24 items-center ${isLeft ? "flex-row-reverse" : "flex-row"}`}>
      <motion.div
        initial={{ opacity: 0, x: isLeft ? 50 : -50 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.8 }}
        className={`w-1/2 px-10 ${isLeft ? "text-left" : "text-right"}`}>
        <div className="inline-block px-4 py-1 rounded-lg bg-[#EAB308] text-black font-black text-xl mb-2 shadow-[0_0_15px_rgba(234,179,8,0.3)]">
          {year}
        </div>
        <h3 className="text-3xl font-extrabold text-white mb-3 uppercase tracking-tighter italic">
          {title}
        </h3>
        <div
          className={`flex flex-col gap-2 ${isLeft ? "items-start" : "items-end"}`}>
          {items.map((item, i) => (
            <p
              key={i}
              className="text-[#A0AEC0] text-lg font-medium bg-[#1A202C]/50 backdrop-blur-sm px-4 py-1 rounded border-l-2 border-[#00BCF0]">
              {item}
            </p>
          ))}
        </div>
      </motion.div>

      <div className="relative flex justify-center items-center z-20">
        <div className="w-12 h-12 rounded-full bg-[#00BCF0] border-4 border-[#0B0F19] shadow-[0_0_25px_rgba(0,188,240,0.5)] flex items-center justify-center font-black text-[#0B0F19]">
          0{index + 1}
        </div>
      </div>

      <div className="w-1/2"></div>
    </div>
  );
};

const About = () => {
  const portfolioData = [
    { year: "2018", title: "Ethical Digit", items: ["Website Development"] },
    {
      year: "2020",
      title: "Application Development",
      items: ["iOS & Android Solutions"],
    },
    {
      year: "2022",
      title: "Product Development",
      items: ["Ecommerce Solutions", "Custom Systems"],
    },
    {
      year: "2023",
      title: "Gateway Solutions",
      items: ["Payment Gateway", "SMS Gateway"],
    },
    {
      year: "2025",
      title: "Connected Group",
      items: ["AI Services", "Enterprise Solutions"],
    },
  ];

  const partners = [
    {
      name: "Huawei Cloud",
      icon: <Cloud className="w-8 h-8" />,
      color: "#E60100",
      category: "Infrastructure",
    },
    {
      name: "AWS",
      icon: <Server className="w-8 h-8" />,
      color: "#FF9900",
      category: "Cloud Hosting",
    },
    {
      name: "Zoom",
      icon: <Video className="w-8 h-8" />,
      color: "#2D8CFF",
      category: "Communication",
    },
    {
      name: "Google Workspace",
      icon: <LayoutGrid className="w-8 h-8" />,
      color: "#4285F4",
      category: "Collaboration",
    },
  ];

  return (
    <div className="bg-[#0B0F19] text-white py-20 font-sans selection:bg-[#00BCF0] overflow-hidden">
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden mb-20">
        <div className="absolute inset-0 z-0">
          <img
            src="/photos/photo3.jpg"
            className="w-full h-full object-cover opacity-20 scale-105 blur-[1px]"
            alt="Background"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19] via-transparent to-[#0B0F19]"></div>
        </div>
        <div className="relative z-10 text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-black uppercase italic tracking-tighter leading-[0.9]">
            Elevate Your <span className="text-[#00BCF0]">Digital</span> <br />
            Transformation
          </motion.h1>
          <p className="mt-8 text-xl text-gray-400 font-bold tracking-[0.3em] uppercase">
            Professional Engineering <span className="text-[#EAB308]">×</span>{" "}
            Innovative Strategy
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 relative mb-40">
        <div className="text-center mb-32 relative">
          <h2 className="text-7xl md:text-9xl font-black text-white/[0.03] absolute left-0 right-0 -top-16 uppercase select-none tracking-tighter">
            Our Journey
          </h2>
          <h2 className="text-4xl font-black relative z-10 uppercase tracking-[0.2em] text-[#00BCF0] italic">
            Proven Portfolio
          </h2>
        </div>

        <div className="relative">
          <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-2 hidden md:block">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 8 1200" // Maintains original aspect ratio base
              preserveAspectRatio="none" // Allows the line to stretch as long as the content
              className="overflow-visible">
              <path
                d="M 4 0 Q 30 200 4 400 T 4 800 T 4 1200"
                stroke="#EAB308"
                strokeWidth="20"
                fill="none"
                strokeDasharray="12 8"
                className="opacity-90  shadow-[0_0_15px_#EAB308]"
              />
            </svg>
          </div>

          <div className="relative z-20">
            {portfolioData.map((data, idx) => (
              <TimelineItem
                key={idx}
                index={idx}
                year={data.year}
                title={data.title}
                items={data.items}
                isLeft={idx % 2 !== 0}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-32 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-[#00BCF0]/5 blur-[120px] rounded-full -z-10" />
        <div className="text-center mb-20">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-[#EAB308] font-black tracking-[0.3em] uppercase text-xs bg-[#EAB308]/10 px-4 py-2 rounded-full">
            Global Network
          </motion.span>
          <h2 className="text-4xl md:text-6xl font-black uppercase italic tracking-tighter mt-6">
            Strategic <span className="text-[#00BCF0]">Alliances</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {partners.map((partner, idx) => (
            <motion.div
              key={idx}
              className="group relative p-10 rounded-[40px] bg-[#161B22]/40 backdrop-blur-2xl border border-white/5 hover:border-[#00BCF0]/30 transition-all duration-700 overflow-hidden">
              <div className="relative z-10 flex flex-col items-center">
                <span className="text-[10px] font-black text-[#00BCF0] tracking-[0.2em] uppercase mb-6">
                  {partner.category}
                </span>
                <div className="w-20 h-20 rounded-2xl bg-[#0B0F19] flex items-center justify-center border border-white/10 mb-6">
                  <div style={{ color: partner.color }}>{partner.icon}</div>
                </div>
                <h4 className="text-xl font-bold text-white">{partner.name}</h4>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default About;
 */
import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Cloud,
  Server,
  Video,
  LayoutGrid,
  Target,
  Eye,
  Heart,
} from "lucide-react";

// --- REUSABLE TIMELINE ITEM ---
const TimelineItem = ({ year, title, items, index, isLeft }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <div
      ref={ref}
      className={`flex w-full mb-24 items-center ${isLeft ? "flex-row-reverse" : "flex-row"}`}>
      <motion.div
        initial={{ opacity: 0, x: isLeft ? 50 : -50 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.8 }}
        className={`w-1/2 px-10 ${isLeft ? "text-left" : "text-right"}`}>
        <div className="inline-block px-4 py-1 rounded-lg bg-[#EAB308] text-black font-black text-xl mb-2 shadow-[0_0_15px_rgba(234,179,8,0.3)]">
          {year}
        </div>
        <h3 className="text-3xl font-extrabold text-white mb-3 uppercase tracking-tighter italic">
          {title}
        </h3>
        <div
          className={`flex flex-col gap-2 ${isLeft ? "items-start" : "items-end"}`}>
          {items.map((item, i) => (
            <p
              key={i}
              className="text-[#A0AEC0] text-lg font-medium bg-[#1A202C]/50 backdrop-blur-sm px-4 py-1 rounded border-l-2 border-[#00BCF0]">
              {item}
            </p>
          ))}
        </div>
      </motion.div>

      <div className="relative flex justify-center items-center z-20">
        <div className="w-12 h-12 rounded-full bg-[#00BCF0] border-4 border-[#0B0F19] shadow-[0_0_25px_rgba(0,188,240,0.5)] flex items-center justify-center font-black text-[#0B0F19]">
          0{index + 1}
        </div>
      </div>
      <div className="w-1/2"></div>
    </div>
  );
};

const About = () => {
  const portfolioData = [
    {
      year: "2018",
      title: "Ethical Digit Founded",
      items: ["Digital Solutions Provider", "Myanmar Market Entry"],
    },
    {
      year: "2020",
      title: "Mobile Innovation",
      items: ["iOS & Android Solutions", "Application Scaling"],
    },
    {
      year: "2022",
      title: "Enterprise Products",
      items: ["Ecommerce Solutions", "Custom ERP Systems"],
    },
    {
      year: "2025",
      title: "Connected Group Evolution",
      items: ["AI Services", "Global Strategic Expansion"],
    },
  ];

  const missionData = [
    {
      title: "Our Mission",
      desc: "To be a successful Digital Transformation Company by providing reliable, innovative, and user-friendly software services in the IT industry.",
      icon: <Target className="w-10 h-10 text-[#00BCF0]" />,
      gradient: "from-blue-500/20 to-transparent",
    },
    {
      title: "Our Vision",
      desc: "Connected Group is at the core of Digital Solutions and Transformation ecosystem, contributing to a strong technology backbone for the future.",
      icon: <Eye className="w-10 h-10 text-[#05DFAD]" />,
      gradient: "from-[#05DFAD]/20 to-[#007BFF]/20",
      featured: true,
    },
    {
      title: "Our Passion",
      desc: "Generate new and innovative ideas that lead the Digital Transformation landscape, strategically evolving platforms for customer satisfaction.",
      icon: <Heart className="w-10 h-10 text-pink-500" />,
      gradient: "from-pink-500/20 to-transparent",
    },
  ];

  const currentYear = new Date().getFullYear();
  const yearsOfExperience = currentYear - 2018;

  const partners = [
    {
      name: "Huawei Cloud",
      icon: <Cloud className="w-8 h-8" />,
      color: "#E60100",
      category: "Infrastructure",
    },
    {
      name: "AWS",
      icon: <Server className="w-8 h-8" />,
      color: "#FF9900",
      category: "Cloud Hosting",
    },
    {
      name: "Zoom",
      icon: <Video className="w-8 h-8" />,
      color: "#2D8CFF",
      category: "Communication",
    },
    {
      name: "Google Workspace",
      icon: <LayoutGrid className="w-8 h-8" />,
      color: "#4285F4",
      category: "Collaboration",
    },
  ];

  return (
    <div className="bg-[#0B0F19] text-white py-20 font-sans selection:bg-[#00BCF0] overflow-hidden">
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden mb-20">
        <div className="absolute inset-0 z-0">
          <img
            src="/photos/hero/bg3.jpg"
            className="w-full h-full object-cover opacity-30 scale-105"
            alt="Office"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19] via-transparent to-[#0B0F19]"></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-5xl">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4`xl md:text-7xl font-black uppercase italic tracking-tighter text-white">
            About <br />{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r  from-cyan-400 to-blue-500">
              Connected Group
            </span>
          </motion.h1>
          <p className="max-w-2xl mx-auto text-gray-400 text-lg md:text-xl font-medium leading-relaxed">
            A blend of young talent and seasoned experts working together to
            create the future of digital commerce.
          </p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-black uppercase italic tracking-tighter leading-none">
                A Legacy of <br />
                <span className="text-[#00BCF0]">Digital Excellence</span>
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed font-medium">
                As a pivotal part of{" "}
                <span className="text-white">
                  {" "}
                  Connected Group, Connected Business Solutions Co., Ltd{" "}
                </span>
                has been at the forefront of Myanmar's digital transformation
                since 2018. Our company is driven by a visionary leadership team
                with over eight years of experience in the ICT industry.
              </p>
            </div>

            <div className="p-8 rounded-[2rem] bg-white/5 border border-white/10 backdrop-blur-md">
              <p className="text-slate-300 italic font-medium leading-relaxed">
                "Our suite of ICT solutions is backed by an extensive data
                network that spans across key business markets in{" "}
                <span className="text-[#05DFAD]">
                  Myanmar, Thailand & Cambodia
                </span>
                ."
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols gap-3">
            <div className="p-10 rounded-[3rem] bg-gradient-to-br from-[#161B22] to-[#0B0F19] border border-white/10 flex flex-col justify-center">
              <h4 className="text-5xl font-black text-[#00BCF0] mb-2">
                {yearsOfExperience}+
              </h4>
              <p className="text-xs uppercase tracking-[0.2em] font-black text-slate-500 mb-1">
                Years Industry Experience
              </p>
              <p className="text-[15px] text-slate-300 font-medium">
                2018 - {currentYear}
              </p>
            </div>
            {/*  <div className="p-10 rounded-[3rem] bg-gradient-to-br from-[#161B22] to-[#0B0F19] border border-white/10 flex flex-col justify-center">
              <h4 className="text-5xl font-black text-[#05DFAD] mb-2">5+</h4>
              <p className="text-xs uppercase tracking-[0.2em] font-black text-slate-500">
                Avg. Executive Tenure
              </p>
            </div> */}
            <div className="col-span-2 p-10 rounded-[3rem] bg-[#00BCF0]/10 border border-[#00BCF0]/20">
              <h4 className="text-xl font-bold text-white mb-4 uppercase tracking-widest">
                Full-Spectrum ICT
              </h4>
              <ul className="grid grid-cols-2 gap-y-2 text-sm text-slate-400 font-bold uppercase tracking-tighter">
                <li>• Software Dev</li>
                <li>• Cloud Managed</li>
                <li>• IT Infrastructure</li>
                <li>• Professional Consulting</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {missionData.map((box, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -10 }}
              className={`relative p-10 rounded-[2.5rem] border ${box.featured ? "border-[#00BCF0] bg-gradient-to-br " + box.gradient : "border-white/10 bg-[#161B22]/50"} overflow-hidden group`}>
              <div className="mb-8">{box.icon}</div>
              <h3 className="text-2xl font-black uppercase italic italic mb-4 tracking-tighter text-white">
                {box.title}
              </h3>
              <p className="text-gray-400 leading-relaxed font-medium">
                {box.desc}
              </p>
              <div
                className={`absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br ${box.gradient} blur-3xl opacity-50`}
              />
            </motion.div>
          ))}
        </div>
      </section>

      {/*  <section className="max-w-6xl mx-auto px-4 relative mb-40">
        <div className="text-center mb-32">
          <h2 className="text-4xl font-black uppercase tracking-[0.2em] text-[#00BCF0] italic mb-4">
            Our Journey
          </h2>
          <p className="text-gray-500 uppercase tracking-widest font-bold text-xs">
            A History of Digital Excellence
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-2 hidden md:block">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 8 1200" // Maintains original aspect ratio base
              preserveAspectRatio="none" // Allows the line to stretch as long as the content
              className="overflow-visible">
              <path
                d="M 4 0 Q 30 200 4 400 T 4 800 T 4 1200"
                stroke="#EAB308"
                strokeWidth="20"
                fill="none"
                strokeDasharray="12 8"
                className="opacity-90  shadow-[0_0_15px_#EAB308]"
              />
            </svg>
          </div>

          <div className="relative z-20">
            {portfolioData.map((data, idx) => (
              <TimelineItem
                key={idx}
                index={idx}
                year={data.year}
                title={data.title}
                items={data.items}
                isLeft={idx % 2 !== 0}
              />
            ))}
          </div>
        </div>
      </section> */}

      {/* --- STRATEGIC ALLIANCE SECTION --- */}
      {/* <section className="max-w-7xl mx-auto px-6 py-32 relative text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-[#00BCF0]/5 blur-[120px] rounded-full -z-10" />
        <h2 className="text-4xl md:text-6xl font-black uppercase italic tracking-tighter mb-20">
          Strategic <span className="text-[#00BCF0]">Alliances</span>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {partners.map((partner, idx) => (
            <motion.div
              key={idx}
              className="group p-10 rounded-[40px] bg-[#161B22]/40 backdrop-blur-2xl border border-white/5 hover:border-[#00BCF0]/30 transition-all duration-700">
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-20 h-20 rounded-2xl bg-[#0B0F19] flex items-center justify-center border border-white/10 mb-6 group-hover:scale-110 transition-transform">
                  <div style={{ color: partner.color }}>{partner.icon}</div>
                </div>
                <h4 className="text-xl font-bold text-white">{partner.name}</h4>
                <span className="text-[10px] font-black text-[#00BCF0] tracking-[0.2em] uppercase mt-2">
                  {partner.category}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </section> */}
    </div>
  );
};

export default About;
